import React, {
	type ChangeEvent,
	Component,
	type ComponentPropsWithoutRef,
	type ComponentType,
	type FC,
	type ReactNode,
} from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { Field, RequiredAsterisk } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { N300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import {
	type ProjectType,
	SERVICE_DESK_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type {
	IntlShapeV2 as IntlShape,
	MessageDescriptorV2,
} from '@atlassian/jira-intl/src/v2/types.tsx';
import ProjectTeamTypeSelect from '@atlassian/jira-servicedesk-project-department-type-select/src/ui/index.tsx';
import type { SelectOption } from '@atlassian/jira-servicedesk-project-department-type-select/src/ui/types.tsx';
import { type AccountId, toAccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { User } from '@atlassian/jira-user-picker-next/src/services/fetch-users/types';
import type { AssigneeType, ProjectCategory } from '../../model/types';
import type { ValidationErrors } from '../../state/ui/types';
import type { AssigneeTypeOption } from './default-assignee/types';
import DefaultAssignee from './default-assignee/view.tsx';
import { Description } from './description/view.tsx';
import type { BaseProps as FormInputProps } from './form-input/view.tsx';
import HistoryKeysField from './historical-keys';
import messages from './messages';
import { ProjectCategoryView } from './project-category/view.tsx';
import ProjectKeyLabelAndPopup from './project-key-label-and-popup';
import ProjectLead from './project-lead/view.tsx';

export type Props = {
	isAdmin: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Avatar: ComponentType<Record<any, any>>;
	FormInput: ComponentType<FormInputProps>;
	projectName: string;
	projectKey: string;
	projectUrl: string;
	projectDescription: JSONDocNode;
	isSimplified: boolean;
	// go/jfe-eslint
	projectType: ProjectType;
	projectTeamType: string;
	projectTeamTypeFreeText: string | undefined;
	projectTypeMessage: MessageDescriptorV2;
	saveEnabled: boolean;
	// go/jfe-eslint
	projectId: number;
	// go/jfe-eslint
	locale: Locale;
	validationErrors: ValidationErrors;
	allProjectCategories: ProjectCategory[];
	selectedProjectCategory: ProjectCategory | undefined;
	projectLeadAccountId: AccountId;
	assigneeType: AssigneeType;
	allowUnassigned: boolean | undefined;
	forceValidationFor: string | undefined;
	onProjectNameChange: (value: string) => void;
	onProjectKeyChange: (value: string) => void;
	onProjectUrlChange: (value: string) => void;
	onProjectDescriptionChange: (value: JSONDocNode) => void;
	onProjectTeamTypeChange: (value: string) => void;
	onProjectTeamTypeFreeTextChange: (value: string | undefined, error: boolean) => void;
	onFieldBlur: () => void;
	onSaveButtonClicked: (arg1: UIAnalyticsEvent) => void;
	onSetProjectCategoryId: (value: string) => void;
	onSetProjectLeadAccountId: (value: AccountId) => void;
	onAssigneeTypeChange: (assigneeType: AssigneeType) => void;
	historicalProjectKeys: string[];
	releasedHistoricalKeys: string[];
	onReleaseProjectKey: (value: string, isSelected: boolean) => void;
	onUndoReleasedProjectKeys: () => void;
};

type PropsWithIntl = Props & {
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
class FormComponent extends Component<PropsWithIntl> {
	state = {
		saveTrigger: 0,
	};

	onProjectNameChange = (e: ChangeEvent<HTMLInputElement>) => {
		this.props.onProjectNameChange(e.target.value);
	};

	onProjectKeyChange = (e: ChangeEvent<HTMLInputElement>) => {
		this.props.onProjectKeyChange(e.target.value);
	};

	onProjectUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
		this.props.onProjectUrlChange(e.target.value);
	};

	onProjectDescriptionChange = (value: JSONDocNode) => {
		this.props.onProjectDescriptionChange(value);
	};

	onAssigneeTypeChange = (assigneeType: AssigneeTypeOption) => {
		this.props.onAssigneeTypeChange(assigneeType.value);
	};

	onProjectCategoryChange = (category?: { value: string; label: string }) => {
		if (category) {
			this.props.onSetProjectCategoryId(category.value);
		} else {
			this.props.onSetProjectCategoryId('-1');
		}
	};

	// The params to this method will be array of users sent from UserPicker. We will be picking the first one.
	// As we have set multi prop false. We will always receive only one user.
	onProjectLeadChange = (users: ReadonlyArray<User>) => {
		const lead = users[0];
		const { onSetProjectLeadAccountId } = this.props;
		onSetProjectLeadAccountId(toAccountId(lead.id));
	};

	onProjectTeamTypeChange = (value: SelectOption | null) => {
		this.props.onProjectTeamTypeChange(value?.value || '');
	};

	onProjectTeamTypeFreeTextChange = (value: string | undefined, error: boolean) => {
		this.props.onProjectTeamTypeFreeTextChange(value, error);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSubmit = (e: any, ...attrs: any[]) => {
		e.preventDefault();
		const { saveEnabled, onSaveButtonClicked } = this.props;
		if (!saveEnabled) {
			return;
		}
		const analyticsEvent = attrs[attrs.length - 1];

		onSaveButtonClicked(analyticsEvent);
	};

	onSaveClick = () => {
		this.setState({ saveTrigger: this.state.saveTrigger + 1 });
	};

	renderProjectLead() {
		const { projectLeadAccountId } = this.props;

		return (
			<ProjectLead
				onChange={this.onProjectLeadChange}
				projectLeadAccountId={projectLeadAccountId}
			/>
		);
	}

	renderProjectCategory() {
		const {
			intl: { formatMessage },
			isAdmin,
			selectedProjectCategory,
			allProjectCategories,
			isSimplified,
		} = this.props;

		return !isAdmin && allProjectCategories.length === 0 ? null : (
			<ProjectCategoryView
				isAdmin={isAdmin}
				label={formatMessage(messages.projectCategoryTitle)}
				placeholder={formatMessage(messages.projectCategoryPlaceholder)}
				allProjectCategories={allProjectCategories}
				onChange={this.onProjectCategoryChange}
				selectedValue={selectedProjectCategory}
				isSimplified={isSimplified}
			/>
		);
	}

	render() {
		const {
			intl: { formatMessage },
			validationErrors,
			Avatar,
			FormInput,
			saveEnabled,
			projectName,
			projectKey,
			historicalProjectKeys,
			releasedHistoricalKeys,
			projectType,
			projectUrl,
			projectTeamType,
			projectTeamTypeFreeText,
			isAdmin,
			projectTypeMessage,
			projectDescription,
			isSimplified,
			assigneeType,
			allowUnassigned,
			forceValidationFor,
			onFieldBlur,
			onReleaseProjectKey,
			onUndoReleasedProjectKeys,
		} = this.props;
		return (
			<FormWithAnalytics onSubmit={this.onSubmit} data-pollinator-details-form>
				<Avatar />
				<FormFieldWrapper>
					<RequiredMessage>
						{formatMessage(messages.requiredFieldsLegend)} <RequiredAsterisk />
					</RequiredMessage>
				</FormFieldWrapper>
				<FormFieldWrapper>
					<FormInput
						label={formatMessage(messages.projectNameLabel)}
						name="projectName"
						message={validationErrors.projectName}
						value={projectName}
						onChange={this.onProjectNameChange}
						onBlur={onFieldBlur}
						focusOnField={forceValidationFor === 'projectName'}
						isRequired
					/>
				</FormFieldWrapper>
				{fg('hix-4189-releasing-historical-project-keys') && (
					<FormFieldWrapper>
						<FormInput
							label={<ProjectKeyLabelAndPopup />}
							name="projectKey"
							message={validationErrors.projectKey}
							value={projectKey}
							onChange={this.onProjectKeyChange}
							onBlur={onFieldBlur}
							focusOnField={forceValidationFor === 'projectKey'}
							isDisabled={!isSimplified && !isAdmin}
							isRequired
						/>
					</FormFieldWrapper>
				)}
				{!fg('hix-4189-releasing-historical-project-keys') && (
					<FormFieldWrapper>
						{!isSimplified ? (
							<FormInput
								label={formatMessage(messages.projectKeyLabel)}
								name="projectKey"
								message={validationErrors.projectKey}
								value={projectKey}
								onChange={this.onProjectKeyChange}
								onBlur={onFieldBlur}
								focusOnField={forceValidationFor === 'projectKey'}
								isDisabled={!isAdmin}
								isRequired
							/>
						) : (
							<FormInput
								label={formatMessage(messages.projectKeyLabel)}
								name="projectKey"
								message={validationErrors.projectKey}
								value={projectKey}
								onChange={this.onProjectKeyChange}
								onBlur={onFieldBlur}
								focusOnField={forceValidationFor === 'projectKey'}
								isRequired
							/>
						)}
					</FormFieldWrapper>
				)}
				{fg('hix-4189-releasing-historical-project-keys') && (
					<FormFieldWrapper>
						{historicalProjectKeys.length > 0 && (isAdmin || isSimplified) && (
							<HistoryKeysField
								historicalProjectKeys={historicalProjectKeys}
								onReleaseProjectKey={onReleaseProjectKey}
								releasedHistoricalKeys={releasedHistoricalKeys}
								onUndoReleasedProjectKeys={onUndoReleasedProjectKeys}
								projectType={projectType}
							/>
						)}
					</FormFieldWrapper>
				)}
				{projectType === SERVICE_DESK_PROJECT && (
					<FormFieldWrapper>
						<ProjectTeamTypeSelect
							onChange={this.onProjectTeamTypeChange}
							onFreeTextChange={this.onProjectTeamTypeFreeTextChange}
							value={projectTeamType}
							freeTextValue={projectTeamTypeFreeText}
							validateRequired
						/>
					</FormFieldWrapper>
				)}
				{!isSimplified && (
					<>
						<FormFieldWrapper>
							<FormInput
								label={formatMessage(messages.projectUrlLabel)}
								name="projectUrl"
								message={validationErrors.projectUrl}
								value={projectUrl}
								onChange={this.onProjectUrlChange}
								onBlur={onFieldBlur}
								focusOnField={forceValidationFor === 'projectUrl'}
							/>
						</FormFieldWrapper>
						<FormFieldWrapper>
							<Field
								label={formatMessage(messages.projectTypeLabel)}
								defaultValue={formatMessage(projectTypeMessage)}
								name="projectType"
								isDisabled
							>
								{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
								{({ fieldProps }: any) => (
									<>
										<Textfield {...fieldProps} />
									</>
								)}
							</Field>
						</FormFieldWrapper>
					</>
				)}
				<FormFieldWrapper>{this.renderProjectCategory()}</FormFieldWrapper>
				{!isSimplified && (
					<FormFieldWrapper>
						<Description
							content={projectDescription}
							onDescriptionChange={this.onProjectDescriptionChange}
							saveTrigger={this.state.saveTrigger}
						/>
					</FormFieldWrapper>
				)}
				<FormFieldWithFieldBaseLabelWrapper>
					{this.renderProjectLead()}
				</FormFieldWithFieldBaseLabelWrapper>
				<FormFieldWrapper>
					<DefaultAssignee
						onChange={this.onAssigneeTypeChange}
						assigneeType={assigneeType}
						allowUnassigned={allowUnassigned}
					/>
				</FormFieldWrapper>
				<Box xcss={buttonWrapperStyles}>
					<Button
						type="submit"
						appearance="primary"
						isDisabled={!saveEnabled}
						onClick={this.onSaveClick}
					>
						{formatMessage(messages.saveFormButtonLabel)}
					</Button>
				</Box>
			</FormWithAnalytics>
		);
	}
}

export default injectIntl(FormComponent);

const buttonWrapperStyles = xcss({
	marginTop: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormFieldWithFieldBaseLabelWrapper = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 44}px`,
	marginTop: token('space.250', '20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormFieldWrapper = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 44}px`,
	/* Form fields which use the Label component from @atlaskit/field-base have a padding-top of 20px. We need to mimic */
	/* this spacing for fields using @atlaskit/form. */
	marginTop: token('space.250', '20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const RequiredMessage = styled.p<{
	children?: ReactNode;
}>({
	font: token('font.body.UNSAFE_small'),
	color: token('color.text.subtle', N300),
});

// eslint-disable-next-line @atlaskit/design-system/no-empty-styled-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Form = styled.form<{
	children?: ReactNode;
}>({});

// type suppression is required to mitigate "TS2742: The inferred type of ... cannot be named without a reference to 'react/node_modules/csstype' happening only with Project References
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FormWithAnalytics: FC<ComponentPropsWithoutRef<typeof Form>> = ComponentWithAnalytics(
	'form',
	{
		onSubmit: 'submitted',
	},
)(Form) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
